import React, { useState } from 'react';
import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import client from '../lib/apolloClient';
import ActionPlanPage from '../components/Succeedo/ActionPlan/ActionPlanPage';


const ACTION_PLAN_QUERY = gql`
  query ActionPlanPage {
    currentUser {
      id
      name
      succeedo_persona
      advisory_team_members {
        id
        role
        status
        name
        firm
      }
      action_plan {
        items(source: "succeedo") {
          id
          title
          description
          status
          priority
          partner_category
          cta_text
          metadata
          source
          documents {
            id
            name
            download_url
          }
        }
        open_count(source: "succeedo")
        completed_count(source: "succeedo")
      }
    }
    active_sprint {
      id
      name
      startOn
      endOn
      status
    }
    value_acceleration_scorecard
    introduction_requests
  }
`;

const UPDATE_ITEM = gql`
  mutation UpdateActionPlanItem($id: ID!, $status: ActionPlanItemStatusEnum) {
    update_action_plan_item(id: $id, status: $status) {
      id
      status
    }
  }
`;

const CREATE_INTRODUCTION_REQUEST = gql`
  mutation CreateIntroductionRequest($partnerCategory: String!, $actionPlanItemId: ID) {
    create_introduction_request(partner_category: $partnerCategory, action_plan_item_id: $actionPlanItemId) {
      introduction_request
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ? parts[0][0] : '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

function computeDaysRemaining(endOn) {
  if (!endOn) return null;
  const end = new Date(endOn);
  const now = new Date();
  const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

function ActionPlanLoader() {
  const { data, loading, refetch } = useQuery(ACTION_PLAN_QUERY);
  const [updateItem] = useMutation(UPDATE_ITEM);
  const [createIntroRequest, { loading: introLoading }] = useMutation(CREATE_INTRODUCTION_REQUEST);

  const user = data?.currentUser;
  const persona = (user?.succeedo_persona || 'seller').toUpperCase();
  const userName = user?.name || '';
  const userInitials = deriveInitials(userName);

  const actionPlan = user?.action_plan || {};
  const actionPlanItems = actionPlan.items || [];
  const openCount = actionPlan.open_count || 0;
  const completedCount = actionPlan.completed_count || 0;
  const total = Math.max(openCount + completedCount, 5);
  const planProgress = {
    completed: completedCount,
    total,
    percentage: total > 0 ? Math.round((completedCount / total) * 100) : 0,
  };

  const sprint = data?.active_sprint;
  const daysRemaining = computeDaysRemaining(sprint?.endOn);
  const sprintExpired = sprint && daysRemaining === 0;

  const scorecard = data?.value_acceleration_scorecard || null;
  const introductionRequests = data?.introduction_requests || [];
  const advisoryTeamMembers = user?.advisory_team_members || [];

  const handleStatusChange = async (itemId, newStatus) => {
    const statusUpper = String(newStatus).toUpperCase();
    try {
      await updateItem({
        variables: { id: itemId, status: statusUpper },
      });
      refetch();
    } catch (e) {
      console.error('Failed to update item status', e);
    }
  };

  const handleRequestIntroduction = async (partnerCategory, actionPlanItemId) => {
    try {
      const { data: result } = await createIntroRequest({
        variables: { partnerCategory, actionPlanItemId },
      });
      const errors = result?.create_introduction_request?.errors || [];
      if (errors.length > 0) {
        console.error('Introduction request errors:', errors);
        return { success: false, errors };
      }
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_introduction_requested', {
          partner_category: partnerCategory,
          action_plan_item_id: actionPlanItemId,
          source: 'action_plan',
        });
      }
      refetch();
      return { success: true };
    } catch (e) {
      console.error('Failed to create introduction request', e);
      return { success: false, errors: ['Something went wrong.'] };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <ActionPlanPage
        scorecard={scorecard}
        items={actionPlanItems}
        daysRemaining={daysRemaining}
        sprintExpired={sprintExpired}
        onStatusChange={handleStatusChange}
        onRequestIntroduction={handleRequestIntroduction}
        introductionRequests={introductionRequests}
        introLoading={introLoading}
        persona={persona}
        userName={userName}
        userInitials={userInitials}
        planProgress={planProgress}
        actionPlanBadge={openCount}
        advisoryTeamMembers={advisoryTeamMembers}
      />
    </>
  );
}

const SucceedoActionPlanContainer = (_props, _railsContext) => (
  <ApolloProvider client={client}>
    <ActionPlanLoader />
  </ApolloProvider>
);

export default SucceedoActionPlanContainer;
